/**
 * Call Center floating operator widget (chat-style bottom panel).
 * Subscribes to CallCenter::OperatorChannel for ringing; accepts via LiveKit.
 */

import { h, render } from 'preact'
import { useEffect, useState, useRef, useCallback } from 'preact/hooks'
import { Room, RoomEvent, Track } from 'livekit-client'
import { subscribe } from '../../../lib/cable'

const t = (key, fallback) => {
  const keys = key.split('.')
  let value = window.I18n?.call_center
  for (const k of keys) {
    value = value?.[k]
    if (value == null) break
  }
  return typeof value === 'string' ? value : (fallback || key)
}

function apiBase(el) {
  return el?.dataset?.callCenterWidgetApiBaseValue ||
    el?.getAttribute('data-call-center-widget-api-base-value') ||
    '/api/call_center'
}

async function apiFetch(base, path, init = {}) {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers || {})
    }
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok, status: res.status, data }
}

export function CallWidgetApp({ apiBasePath }) {
  const [offer, setOffer] = useState(null) // { callSessionId, callerContext, timeoutSeconds }
  const [active, setActive] = useState(null) // { callSessionId, room, callerContext }
  const [error, setError] = useState(null)
  const [minimized, setMinimized] = useState(false)
  const [duration, setDuration] = useState(0)
  const roomRef = useRef(null)

  useEffect(() => {
    const sub = subscribe(
      { channel: 'CallCenter::OperatorChannel' },
      {
        received(data) {
          if (!data || !data.type) return
          if (data.type === 'call_ringing') {
            setOffer({
              callSessionId: data.call_session_id,
              callerContext: data.caller_context,
              timeoutSeconds: data.timeout_seconds || 30,
              roomName: data.room_name
            })
            setMinimized(false)
            setError(null)
          } else if (data.type === 'call_taken') {
            setOffer((prev) =>
              prev && prev.callSessionId === data.call_session_id ? null : prev
            )
          } else if (data.type === 'call_ended') {
            setOffer((prev) =>
              prev && prev.callSessionId === data.call_session_id ? null : prev
            )
            setActive((prev) => {
              if (prev && prev.callSessionId === data.call_session_id) {
                try { roomRef.current?.disconnect() } catch (_) { /* ignore */ }
                roomRef.current = null
                return null
              }
              return prev
            })
          }
        }
      }
    )
    return () => sub.unsubscribe()
  }, [])

  useEffect(() => {
    if (!active) {
      setDuration(0)
      return undefined
    }
    const id = setInterval(() => setDuration((d) => d + 1), 1000)
    return () => clearInterval(id)
  }, [active])

  const accept = useCallback(async () => {
    if (!offer) return
    setError(null)
    const { ok, status, data } = await apiFetch(
      apiBasePath,
      `/call_sessions/${offer.callSessionId}/accept`,
      { method: 'POST', body: '{}' }
    )
    if (!ok) {
      setError(
        status === 409
          ? t('widget.already_taken', 'Call already taken')
          : (data.error || t('widget.accept_failed', 'Failed to accept call'))
      )
      if (status === 409) setOffer(null)
      return
    }

    try {
      const room = new Room()
      roomRef.current = room
      await room.connect(data.livekit_url, data.livekit_token)
      await room.localParticipant.setMicrophoneEnabled(true)
      room.on(RoomEvent.Disconnected, () => {
        setActive(null)
        roomRef.current = null
      })
      // Subscribe to remote audio
      room.remoteParticipants.forEach((p) => {
        p.audioTrackPublications.forEach((pub) => {
          if (pub.track) {
            const el = pub.track.attach()
            el.autoplay = true
            document.body.appendChild(el)
          }
        })
      })
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach()
          el.autoplay = true
          document.body.appendChild(el)
        }
      })

      setActive({
        callSessionId: offer.callSessionId,
        callerContext: data.caller_context || offer.callerContext,
        room
      })
      setOffer(null)
    } catch (err) {
      console.error(err)
      setError(t('widget.connect_failed', 'Failed to connect audio'))
    }
  }, [offer, apiBasePath])

  const hangup = useCallback(async () => {
    if (!active) return
    try {
      await apiFetch(apiBasePath, `/call_sessions/${active.callSessionId}/hangup`, {
        method: 'POST',
        body: '{}'
      })
    } catch (_) { /* ignore */ }
    try { roomRef.current?.disconnect() } catch (_) { /* ignore */ }
    roomRef.current = null
    setActive(null)
  }, [active, apiBasePath])

  const decline = useCallback(() => {
    setOffer(null)
  }, [])

  if (!offer && !active) return null

  const ctx = (active || offer)?.callerContext
  const panelRight = 104

  if (minimized) {
    return (
      <button
        type="button"
        className="call-float-minimized"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: `${panelRight}px`,
          zIndex: 1065,
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          border: 'none',
          background: active ? '#28a745' : '#6f42c1',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}
        onClick={() => setMinimized(false)}
        title={t('widget.incoming_call', 'Incoming call')}
      >
        <i className="bi bi-telephone-fill" />
      </button>
    )
  }

  const formatDuration = (s) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  return (
    <div
      className="call-float-panel"
      style={{
        position: 'fixed',
        bottom: '0',
        right: `${panelRight}px`,
        zIndex: 1065,
        width: '340px',
        maxHeight: '70vh',
        background: 'white',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -2px 20px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          background: active ? '#28a745' : '#6f42c1',
          color: 'white',
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span className="fw-semibold">
          {active
            ? t('widget.call_in_progress', 'Call in progress')
            : t('widget.incoming_call', 'Incoming call')}
        </span>
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          onClick={() => setMinimized(true)}
        >
          <i className="bi bi-dash-lg" />
        </button>
      </div>

      <div style={{ padding: '14px', overflowY: 'auto', flex: 1 }}>
        {error && (
          <div className="alert alert-danger alert-sm py-2">{error}</div>
        )}

        <CallerContextView ctx={ctx} />

        {active && (
          <div className="text-center my-3" style={{ fontSize: '1.4rem', fontWeight: 600, color: '#28a745' }}>
            {formatDuration(duration)}
          </div>
        )}

        <div className="d-flex gap-2 mt-3">
          {offer && !active && (
            <>
              <button type="button" className="btn btn-success flex-grow-1" onClick={accept}>
                <i className="bi bi-telephone-fill me-1" />
                {t('widget.accept', 'Accept')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={decline}>
                {t('widget.dismiss', 'Dismiss')}
              </button>
            </>
          )}
          {active && (
            <button type="button" className="btn btn-danger w-100" onClick={hangup}>
              <i className="bi bi-telephone-x-fill me-2" />
              {t('widget.end_call', 'End call')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CallerContextView({ ctx }) {
  if (!ctx) {
    return <p className="text-muted mb-0">{t('widget.no_context', 'No caller details')}</p>
  }

  const person = ctx.person
  const bc = ctx.building_complex
  const routing = ctx.routing

  return (
    <div className="caller-context">
      <div className="mb-2">
        <div className="fw-semibold">{ctx.caller_phone_number}</div>
        {person?.name && <div className="small">{person.name}</div>}
        {person?.email && <div className="small text-muted">{person.email}</div>}
      </div>

      {bc && (
        <div className="mb-2 small">
          <span className="text-muted">{t('widget.building_complex', 'Building complex')}: </span>
          <strong>{bc.name}</strong>
        </div>
      )}

      {routing?.reason && (
        <div className="mb-2">
          <span className="badge bg-light text-dark">
            {routing.reason === 'building_complex_responsible'
              ? t('widget.routing_bc', 'BC responsible')
              : t('widget.routing_group', 'Person group')}
            {routing.person_group?.name ? `: ${routing.person_group.name}` : ''}
          </span>
        </div>
      )}

      {ctx.ivr_flow?.name && (
        <div className="small text-muted mb-2">
          IVR: {ctx.ivr_flow.name}
        </div>
      )}

      {Array.isArray(ctx.properties) && ctx.properties.length > 0 && (
        <div className="mb-2">
          <div className="small text-muted">{t('widget.properties', 'Properties')}</div>
          <ul className="small mb-0 ps-3">
            {ctx.properties.slice(0, 5).map((p) => (
              <li key={p.id}>
                {[p.name, p.door_number, p.code].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(ctx.transcript_preview) && ctx.transcript_preview.length > 0 && (
        <div className="mb-2">
          <div className="small text-muted">{t('widget.transcript', 'Recent talk')}</div>
          <div
            className="small border rounded p-2 bg-light"
            style={{ maxHeight: '120px', overflowY: 'auto' }}
          >
            {ctx.transcript_preview.map((line, i) => (
              <div key={i}>
                <strong>{line.role}:</strong> {line.content}
              </div>
            ))}
          </div>
        </div>
      )}

      {ctx.collected_data && Object.keys(ctx.collected_data).length > 0 && (
        <div className="small text-muted">
          {Object.entries(ctx.collected_data)
            .filter(([k]) => !['transfer_status', 'offered_to_person_ids', 'accepted_by_person_id', 'operator_offer_started_at', 'operator_offer_timeout_seconds', 'routing_reason'].includes(k))
            .slice(0, 8)
            .map(([k, v]) => (
              <div key={k}><code>{k}</code>: {String(v)}</div>
            ))}
        </div>
      )}
    </div>
  )
}

export function mountCallWidget(el) {
  if (!el) return
  const base = apiBase(el)
  render(<CallWidgetApp apiBasePath={base} />, el)
}

export function unmountCallWidget(el) {
  if (el) render(null, el)
}
